import {
  Alert,
  Flexbox,
  Option,
  Select,
  Sentiments,
  SingleDatePicker,
} from '@sede-enterprise/shell-ds-react-framework';
import { AlertCircle } from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Icon/components';
import {
  createSPProductList,
  isDateInMarchClockChange,
  isDateInOctoberClockChange,
  UK_TIMEZONE,
} from '@see-power-pft-common-js/utils/product-mapper';
import { clampUtcDateToSameOrPrevSp } from '@see-power-pft-common-js/utils/sp';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import dayjs from 'dayjs';
import { ISODateStringUTC } from 'pages/Availability/types';
import styled from 'styled-components';

const ClockChangeIcon = styled(AlertCircle)`
  color: ${({ theme }) =>
    theme.getLayer({ sentiment: Sentiments.Warning })['Background/Sentiment']};
  flex-shrink: 0;
`;

interface DateTimePickerProps {
  ISOStringUTCValue: ISODateStringUTC | null;
  onChange: (datetime: ISODateStringUTC | null) => void;
  disableTime?: boolean;
  disableDate?: boolean;
  testId?: string;
}

type SPTimePeriod = {
  ukTimeValueStart: string;
  utcTimeValueStart: string;
  utcTimeStart: string;
};

const getSPTimePeriods = (date: Date): SPTimePeriod[] => {
  const spProductList = createSPProductList(date);

  const timePeriods = spProductList.map((product) => {
    return {
      ukTimeValueStart: formatInTimeZone(
        product.ukTimeStart,
        UK_TIMEZONE,
        'HH:mm',
      ),
      utcTimeValueStart: formatInTimeZone(product.utcTimeStart, 'UTC', 'HH:mm'),
      utcTimeStart: product.utcTimeStart,
    };
  });

  return timePeriods;
};

const isClockChangeDate = (
  isoDateStringUTC: ISODateStringUTC | null,
): boolean => {
  if (!isoDateStringUTC) {
    return false;
  }

  const date = new Date(isoDateStringUTC);
  const marchChange = isDateInMarchClockChange(date);
  const octoberChange = isDateInOctoberClockChange(date);
  return marchChange || octoberChange;
};

export const DateTimeSPPicker = (props: DateTimePickerProps) => {
  const date = props.ISOStringUTCValue
    ? new Date(props.ISOStringUTCValue)
    : new Date();

  const dateRoundedToSP = clampUtcDateToSameOrPrevSp(date);
  const SPTimePeriods = getSPTimePeriods(dateRoundedToSP);

  return (
    <Flexbox flexDirection="column">
      <Flexbox flexDirection="row">
        <SingleDatePicker
          data-testid={props.testId ? `${props.testId}-date` : undefined}
          placeholder="DD MM YYYY"
          value={
            props.ISOStringUTCValue
              ? dayjs(
                  formatInTimeZone(
                    props.ISOStringUTCValue,
                    UK_TIMEZONE,
                    'yyyy-MM-dd',
                  ),
                )
              : null
          }
          onChange={(value) => {
            if (value) {
              // Drop the time component so that only the date is used from the picker
              const dateStr = value.format('YYYY-MM-DD');

              // Convert the selected date to UTC, as if it were in UK timezone
              // This is so that upon selecting a date, the time is set to 00:00 UK time on that date no matter the time of year
              const utcDate = fromZonedTime(dateStr, UK_TIMEZONE);
              props.onChange(utcDate.toISOString());
            }
          }}
          disabled={props.disableDate}
        />
        <div style={{ width: '100%', margin: '0px 8px' }}>
          <Select
            data-testid={props.testId ? `${props.testId}-time` : undefined}
            placeholder="--:--"
            value={props.ISOStringUTCValue ?? null}
            onChange={(selectedSPISOStringUTC: ISODateStringUTC) => {
              props.onChange(selectedSPISOStringUTC);
            }}
            allowClear={false}
            disabled={props.disableTime}
            virtual={false}
            getPopupContainer={(trigger) =>
              trigger.parentElement?.parentElement || document.body
            }
          >
            {SPTimePeriods.map((SPTimePeriod) => (
              <Option
                key={SPTimePeriod.utcTimeStart}
                value={SPTimePeriod.utcTimeStart} // The on change handler takes the UTC ISO string for the SP
              >
                <div>{SPTimePeriod.ukTimeValueStart}</div>

                {/* On clock changeover day, show the utc time as well */}
                {props.ISOStringUTCValue !== null &&
                  isClockChangeDate(props.ISOStringUTCValue) && (
                    <div
                      style={{
                        fontSize: '0.8em',
                        color: '#888',
                        lineHeight: '0.5em',
                        padding: '4px 0',
                      }}
                    >
                      {`${SPTimePeriod.utcTimeValueStart} UTC`}
                    </div>
                  )}
              </Option>
            ))}
          </Select>
        </div>
      </Flexbox>
      {isClockChangeDate(props.ISOStringUTCValue) && (
        <Alert
          sentiment={Sentiments.Warning}
          solidBgColor
          iconVisibility={false}
          role="status"
          style={{
            marginTop: '8px',
            minHeight: '30px',
            paddingTop: '0px',
            paddingBottom: '0px',
          }}
          data-testid={
            props.testId ? `${props.testId}-clock-change-alert` : undefined
          }
        >
          <Flexbox
            alignItems="center"
            gap="8px"
            justifyContent="flex-start"
            style={{ width: '100%', textAlign: 'left' }}
          >
            <ClockChangeIcon aria-hidden="true" focusable="false" />
            <span>This is a clock change date.</span>
          </Flexbox>
        </Alert>
      )}
    </Flexbox>
  );
};
